/**
 * صوت التنبيهات: نغمةٌ مركّبة بـ WebAudio (بلا ملف صوتي) تُعزف مع كل إشعارٍ يصل.
 *
 * المتصفحات تمنع الصوت قبل أول تفاعلٍ للموظف مع الصفحة، وSafari خصوصاً يُبقي كل سياق صوتٍ
 * يُنشأ خارج نقرةٍ معلَّقاً إلى الأبد — فلم تكن النغمة تُسمع فيه. لذا سياقٌ واحد مشترك يُفتح
 * مع أول نقرة أو مفتاح ويبقى جاهزاً، فتُعزف النغمة بعدها ولو كان التبويب بالخلفية والموظف
 * في برنامجٍ آخر.
 *
 * ويتنسّق مع عامل الخدمة (`public/sw.js`): قبل عرض تنبيه النظام يسأل الصفحة المفتوحة هل
 * عزفت النغمة وهل هي أمام الموظف فعلاً — فيأتي تنبيه النظام صامتاً إن رنّت الصفحة (لا صوتان
 * للخبر الواحد)، وبصوت النظام إن لم تستطع.
 */

type AudioCtor = typeof AudioContext

/** رسالة عامل الخدمة للصفحة عند وصول دفعٍ، وردّها عليه */
export interface PushChimeRequest {
  type: 'ufuq:push'
  id: number | null
}
export interface PushChimeReply {
  chimed: boolean
  focused: boolean
}

const CROSS_TAB_KEY = 'ufuq_chime_last'
const CROSS_TAB_WINDOW_MS = 15_000
const RESUME_TIMEOUT_MS = 400
const GESTURES = ['pointerdown', 'keydown', 'touchend'] as const

let ctx: AudioContext | null = null
let ready = false
let started = false
const listeners = new Set<() => void>()
/** نتيجة نغمة كل إشعار — الخبر يصل من الاتصال الحيّ ومن عامل الخدمة معاً فيُعزف مرة */
const chimes = new Map<number, Promise<boolean>>()

function setReady(value: boolean) {
  if (ready === value) return
  ready = value
  listeners.forEach((l) => l())
}

/** الصوت مسموحٌ الآن — وإلا يُعرض زر «تفعيل صوت التنبيهات» */
export function isAlertSoundReady(): boolean {
  return ready
}

export function subscribeAlertSound(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function audioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext ||
    null
  )
}

/** تفاعلٌ سابق مع الصفحة يسمح بتشغيل الصوت بلا نقرةٍ جديدة (Chrome) */
function hasBeenActive(): boolean {
  const ua = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } })
    .userActivation
  return ua?.hasBeenActive === true
}

function context(create: boolean): AudioContext | null {
  if (ctx || !create) return ctx
  const Ctor = audioCtor()
  if (!Ctor) return null
  try {
    const c = new Ctor()
    c.addEventListener?.('statechange', () => setReady(isRunning(c)))
    ctx = c
  } catch {
    ctx = null
  }
  return ctx
}

/** حالة السياق تُقرأ حيّةً — تتبدّل بعد resume() بلا علم المُدقِّق */
function isRunning(c: AudioContext): boolean {
  return c.state === 'running'
}

function withTimeout(p: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms)
    p.then(
      () => {
        clearTimeout(t)
        resolve()
      },
      (err: unknown) => {
        clearTimeout(t)
        reject(err)
      },
    )
  })
}

/**
 * بنقرة الموظف أو مفتاحه: يُفتح السياق ويُعزف صمتٌ قصير داخل النقرة نفسها — Safari
 * يشترطه ليبقى الصوت مسموحاً بعدها. يعيد جاهزية الصوت.
 */
export async function unlockAlertSound(): Promise<boolean> {
  const c = context(true)
  if (!c) return false
  if (isRunning(c)) {
    setReady(true)
    return true
  }
  try {
    const src = c.createBufferSource()
    src.buffer = c.createBuffer(1, 1, 22050)
    src.connect(c.destination)
    src.start(0)
  } catch {
    /* بعض المتصفحات لا تحتاج العيّنة — الاستئناف يكفي */
  }
  try {
    await withTimeout(c.resume(), RESUME_TIMEOUT_MS)
  } catch {
    /* بقي معلَّقاً — الزر يبقى ظاهراً */
  }
  setReady(isRunning(c))
  return ready
}

/** نغمتان صافيتان متصاعدتان (سي ← مي) بتوافقياتٍ خفيفة — واضحة دون إزعاج */
function ring(c: AudioContext) {
  const t0 = c.currentTime + 0.02
  const note = (freq: number, at: number, dur: number) => {
    for (const [mult, peak] of [
      [1, 0.3],
      [2, 0.07],
      [3, 0.025],
    ] as const) {
      const o = c.createOscillator()
      const g = c.createGain()
      o.type = 'sine'
      o.frequency.value = freq * mult
      g.gain.setValueAtTime(0.0001, t0 + at)
      g.gain.exponentialRampToValueAtTime(peak, t0 + at + 0.012)
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur)
      o.connect(g).connect(c.destination)
      o.start(t0 + at)
      o.stop(t0 + at + dur + 0.05)
    }
  }
  note(988, 0, 0.45)
  note(1319, 0.14, 0.65)
}

/** تعزف نغمة التنبيه إن كان الصوت مسموحاً — ويعيد هل عُزفت فعلاً */
export async function playAlertChime(): Promise<boolean> {
  const c = context(ready || hasBeenActive())
  if (!c) return false
  if (!isRunning(c)) {
    try {
      await withTimeout(c.resume(), RESUME_TIMEOUT_MS)
    } catch {
      return false
    }
    if (!isRunning(c)) return false
  }
  setReady(true)
  try {
    ring(c)
    return true
  } catch {
    return false
  }
}

function chimedInAnotherTab(id: number): boolean {
  try {
    const raw = localStorage.getItem(CROSS_TAB_KEY)
    if (!raw) return false
    const last = JSON.parse(raw) as { id?: number; at?: number }
    return last.id === id && Date.now() - (last.at ?? 0) < CROSS_TAB_WINDOW_MS
  } catch {
    return false
  }
}

function markChimed(id: number) {
  try {
    localStorage.setItem(CROSS_TAB_KEY, JSON.stringify({ id, at: Date.now() }))
  } catch {
    /* تخزين محظور — أسوأ الحالات نغمتان من تبويبين */
  }
}

/**
 * نغمة إشعارٍ واحدة مهما تعددت طرق وصول خبره (الاتصال الحيّ، عامل الخدمة، تبويبٌ آخر) —
 * ويعيد هل رنّ هذا المتصفح له.
 */
export function chimeForNotification(id: number | null | undefined): Promise<boolean> {
  if (!id) return playAlertChime()
  const known = chimes.get(id)
  if (known) return known
  const result = chimedInAnotherTab(id)
    ? Promise.resolve(true)
    : playAlertChime().then((ok) => {
        if (ok) markChimed(id)
        return ok
      })
  chimes.set(id, result)
  if (chimes.size > 50) {
    const oldest = chimes.keys().next().value
    if (oldest !== undefined) chimes.delete(oldest)
  }
  return result
}

/** عامل الخدمة يسأل عند كل دفع: نعزف النغمة (إن لم تُعزف) ونخبره بتركيز الصفحة الحقيقي */
function onServiceWorkerMessage(event: MessageEvent) {
  const m = event.data as Partial<PushChimeRequest> | null
  if (!m || m.type !== 'ufuq:push') return
  const port = event.ports?.[0]
  const focused = document.visibilityState === 'visible' && document.hasFocus()
  void chimeForNotification(m.id ?? null).then((chimed) => {
    const reply: PushChimeReply = { chimed, focused }
    port?.postMessage(reply)
  })
}

/** يُستدعى مرة مع دخول النظام: فتح الصوت مع أول تفاعل، والإجابة على أسئلة عامل الخدمة */
export function initAlertSound() {
  if (started || typeof window === 'undefined') return
  started = true
  const unlock = () => {
    if (ctx && isRunning(ctx)) return
    void unlockAlertSound()
  }
  GESTURES.forEach((ev) => window.addEventListener(ev, unlock, { capture: true, passive: true }))
  if ('serviceWorker' in navigator && navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage)
  }
}
